import { Sparkles } from "lucide-react";
import { useState, type FormEvent } from "react";

import { BoardFine, BoardPanel } from "../board/Board";
import { Button } from "../ui/button";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import type { GoalDraft, ManagedGoal } from "./goalModel";

/**
 * Mint a subgoal under a parent: a title, the outcome, and the acceptance
 * condition — the Cleo Can, fixed at the mint (the door refuses without one).
 * Creating a goal never launches work (GF3).
 */
export function GoalDraftForm({
  parent,
  onCreate,
  onCancel,
}: {
  readonly parent: ManagedGoal;
  readonly onCreate: (draft: GoalDraft) => Promise<boolean>;
  readonly onCancel: () => void;
}) {
  const [busy, setBusy] = useState(false);
  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (busy) return;
    const data = new FormData(event.currentTarget);
    setBusy(true);
    try {
      if (
        await onCreate({
          parentGoalId: parent.goalId,
          title: String(data.get("title")).trim(),
          statement: String(data.get("statement")).trim(),
          cleoCan: String(data.get("cleoCan")).trim(),
        })
      )
        onCancel();
    } finally {
      setBusy(false);
    }
  }
  return (
    <BoardPanel
      render="form"
      glyph={<Sparkles />}
      title={`New subgoal under ${parent.title}`}
      onSubmit={(event) => void submit(event)}
      data-goal-draft=""
    >
      <div className="grid gap-3">
        <Input
          nativeInput
          name="title"
          aria-label="Goal title"
          placeholder="Goal title"
          required
          maxLength={160}
        />
        <Textarea
          name="statement"
          aria-label="Goal outcome"
          placeholder="What outcome are we committing to?"
          required
          maxLength={4000}
          rows={3}
        />
        <Textarea
          name="cleoCan"
          aria-label="Acceptance condition"
          placeholder="What observable evidence will prove success?"
          required
          maxLength={4000}
          rows={3}
        />
      </div>
      <div className="flex items-center gap-2">
        <Button type="submit" size="sm" disabled={busy}>
          {busy ? "Saving…" : "Create goal"}
        </Button>
        <Button type="button" variant="ghost-muted" size="sm" onClick={onCancel} disabled={busy}>
          Cancel
        </Button>
      </div>
      <BoardFine>Creates the goal. Sessions can be assigned when you are ready.</BoardFine>
    </BoardPanel>
  );
}
